/* eslint-disable @atlaskit/platform/no-preconditioning -- Existing chat-store actions gate feature behavior from runtime flags. */

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	CHAT_STORE_STATUS,
	type Conversation,
	isStoreAssistant,
	type StoreAssistant,
	type StoreChatItem,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { setConversationCancelled } from '@atlassian/conversation-assistant-store/controllers/conversation-cancellation';

import { abortPreviewCardRenderExperience } from '../../common/utils/abort-preview-card-render-experience';
import { clearStatus } from './clearStatus';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { getSendMessageExperienceName } from './getSendMessageExperienceName';
import { markInFlightActionsAsCancelled } from './markInFlightActionsAsCancelled';
import { setConversationData } from './setConversationData';
import { activeAssistantMessageIds } from './store-abort-controller/active-assistant-message-id';
import type { ChatStoreAction } from './types';

type AbortRequestsArgs = {
	conversationId?: string;
	deepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	fastModeEnabled?: boolean;
	taskModeEnabled?: boolean;
};

const markAssistantMessageAsAborted = (
	messages: StoreChatItem[],
	assistantMessageId: string | undefined,
): StoreChatItem[] => {
	if (!assistantMessageId) {
		return messages;
	}

	let hasChanged = false;
	const nextMessages = messages.map((message) => {
		if (
			!isStoreAssistant(message) ||
			message.content.message.message.id !== assistantMessageId ||
			message.isAborted === true
		) {
			return message;
		}

		hasChanged = true;
		return { ...message, isAborted: true };
	});

	return hasChanged ? nextMessages : messages;
};

// Abort requests for a specific conversation, or ALL conversations if no conversationId is passed
export const abortRequests: SafeAction<[AbortRequestsArgs?]> = createSafeAction(
	StoreActionLoggableName.abortRequests,
	({
		conversationId,
		thinkDeeperEnabled,
		fastModeEnabled,
		taskModeEnabled,
		deepResearchEnabled,
	}: AbortRequestsArgs = {}): ChatStoreAction =>
		(
			{ getState, dispatch },
			{
				experienceTracker,
				setReasoningMode,
				isReconnecting,
				unifiedTasksActions,
				markIncompleteResearchesErrored,
				clearMessageResearch,
				getMessages,
				setMessages,
			},
		) => {
			const conversations = getState().conversations;

			const abortConversationStream = (conversation: Conversation) => {
				const shouldSynchronouslyCancelRemix =
					conversation?.isViewModeRemix === true &&
					isExperimentEnabled('cc_maui_polish_changes_batch_5');

				if (
					conversation?.id &&
					(expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true) ||
						shouldSynchronouslyCancelRemix)
				) {
					dispatch(
						setConversationData({
							conversationId: conversation.id,
							data: clearStatus({ lastAgentMessageStatus: 'CANCELLED' }),
						}),
					);

					// Only clear research when answer streaming has started.
					if (conversation.status === CHAT_STORE_STATUS.typingResponse) {
						const streamingAssistantMessageIds = getMessages(conversation.id)
							.filter(
								(message): message is StoreAssistant =>
									isStoreAssistant(message) && message.content.streamingComplete !== true,
							)
							.map((message) => message.content.message.message.id);
						streamingAssistantMessageIds.forEach((messageId) => {
							markIncompleteResearchesErrored({ messageId });
							clearMessageResearch({ messageId });
						});
					}
				}

				if (conversation?.storeAbortController) {
					experienceTracker.start({
						name: ExperienceName.ABORT_AGENT_STREAM,
						id: conversation?.id,
						attributes: {
							conversationId: conversation?.id,
							isReconnecting: isReconnecting(conversation?.id ?? ''),
						},
					});

					abortPreviewCardRenderExperience({
						experienceTracker,
						reason: 'User cancelled before preview card rendered',
						terminalReason: 'user-cancelled',
						attributes: {
							conversationId: conversation.id,
							isProgrammaticAbort: true,
						},
					});

					if (conversation.id) {
						const currentMessages = getMessages(conversation.id);
						let nextMessages = markAssistantMessageAsAborted(
							currentMessages,
							activeAssistantMessageIds.get(conversation.storeAbortController),
						);
						if (!isReconnecting(conversation.id)) {
							nextMessages = markInFlightActionsAsCancelled(nextMessages);
						}
						if (nextMessages !== currentMessages) {
							setMessages(conversation.id, nextMessages);
						}
					}

					conversation.storeAbortController.abort();
					if (conversation.id && isExperimentEnabled('platform_editor_inline_agentic_comments')) {
						setConversationCancelled(conversation.id, true);
					}

					experienceTracker.abort({
						name: getSendMessageExperienceName({
							deepResearchEnabled,
							thinkDeeperEnabled,
							taskModeEnabled,
							fastModeEnabled,
						}),
						reason: 'Aborted',
						attributes: {
							isProgrammaticAbort: true,
						},
					});

					experienceTracker.succeed({
						name: ExperienceName.ABORT_AGENT_STREAM,
					});

					if (
						taskModeEnabled &&
						conversation?.id &&
						unifiedTasksActions &&
						fg('rovo_chat_enable_tasks')
					) {
						unifiedTasksActions.setTaskProgressState({
							conversationId: conversation.id,
							progressState: 'Cancelled',
							updatedAt: new Date().toISOString(),
						});
					}
				}
			};

			if (conversationId) {
				abortConversationStream(conversations[conversationId]);
			} else {
				Object.values(conversations).forEach(abortConversationStream);
			}
		},
	{
		experienceName: () => ExperienceName.ABORT_AGENT_STREAM,
	},
);
